import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase,
  Plus,
  CheckCircle,
  Circle,
  Search,
  AlertCircle,
  FileText,
  Heart,
  Shield,
  Plane,
  Home,
  Trash2,
  Edit
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { EmergencyService, EmergencyPack, EmergencyProfile } from '../../lib/emergencyService';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { SearchInput } from '../common/SearchInput';

const packTypeLabels: Record<EmergencyPack['pack_type'], { label: string; icon: React.ReactNode; color: string }> = {
  medical: { label: 'Medical Pack', icon: <Heart size={20} />, color: 'bg-red-100 text-red-800 border-red-200' },
  property_claim: { label: 'Property Claim Pack', icon: <Home size={20} />, color: 'bg-blue-100 text-blue-800 border-blue-200' },
  travel: { label: 'Travel Pack', icon: <Plane size={20} />, color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  child_school: { label: 'Child/School Pack', icon: <Shield size={20} />, color: 'bg-purple-100 text-purple-800 border-purple-200' },
  sme_first_48h: { label: 'SME First 48h Pack', icon: <Briefcase size={20} />, color: 'bg-amber-100 text-amber-800 border-amber-200' }
};

export function EmergencyKit() {
  const { user } = useAuth();
  const [packs, setPacks] = useState<EmergencyPack[]>([]);
  const [profile, setProfile] = useState<EmergencyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPack, setEditingPack] = useState<EmergencyPack | null>(null);
  const [selectedPack, setSelectedPack] = useState<EmergencyPack | null>(null);

  useEffect(() => {
    loadProfileAndPacks();
  }, [user]);

  const loadProfileAndPacks = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get or create active profile
      let activeProfile = await EmergencyService.getActiveProfile(user.id);
      
      if (!activeProfile) {
        activeProfile = await EmergencyService.createProfile({
          user_id: user.id,
          type: 'personal',
          status: 'active',
          locale: 'en-AU',
          name: 'My Emergency Plan',
          description: 'Personal emergency preparedness plan'
        });
        await EmergencyService.activateProfile(activeProfile.id);
      }
      
      setProfile(activeProfile);
      
      // Load packs
      const packsData = await EmergencyService.getPacks(activeProfile.id);
      setPacks(packsData);
    } catch (err) {
      console.error('Error loading packs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load emergency packs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePack = async (pack: Omit<EmergencyPack, 'id' | 'created_at' | 'updated_at' | 'share_token'>) => {
    if (!profile) return;
    
    try {
      const newPack = await EmergencyService.createPack({
        ...pack,
        profile_id: profile.id
      });
      setPacks(prev => [...prev, newPack]);
      setShowAddModal(false);
    } catch (err) {
      console.error('Error creating pack:', err);
      setError(err instanceof Error ? err.message : 'Failed to create pack');
    }
  };

  const handleUpdatePack = async (pack: EmergencyPack) => {
    try {
      const updatedPack = await EmergencyService.updatePack(pack.id, pack);
      setPacks(prev => prev.map(p => p.id === updatedPack.id ? updatedPack : p));
      setEditingPack(null);
    } catch (err) {
      console.error('Error updating pack:', err);
      setError(err instanceof Error ? err.message : 'Failed to update pack');
    }
  };

  const handleDeletePack = async (packId: string) => {
    if (!confirm('Are you sure you want to delete this emergency pack? This action cannot be undone.')) {
      return;
    }

    try {
      await EmergencyService.deletePack(packId);
      setPacks(prev => prev.filter(p => p.id !== packId));
    } catch (err) {
      console.error('Error deleting pack:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete pack');
    }
  };

  const filteredPacks = packs.filter(pack => {
    const matchesSearch = pack.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (pack.description && pack.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesType = typeFilter === 'all' || pack.pack_type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-64 bg-gray-200 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Emergency Kit</h1>
            <p className="text-xl text-gray-600">Manage your emergency information packs</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            variant="primary"
            className="flex items-center space-x-2 px-6 py-3 text-lg"
          >
            <Plus size={20} />
            <span>New Pack</span>
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1">
            <SearchInput
              placeholder="Search packs by name or description..."
              value={searchQuery}
              onChange={setSearchQuery}
              icon={<Search size={18} />}
              className="text-lg"
            />
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent text-lg"
            >
              <option value="all">All Types</option>
              {Object.entries(packTypeLabels).map(([key, value]) => (
                <option key={key} value={key}>{value.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Packs Grid */}
        <AnimatePresence>
          {filteredPacks.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-white border-0 shadow-xl">
                <div className="text-center py-16">
                  <div className="mx-auto w-24 h-24 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mb-6">
                    <Briefcase className="h-12 w-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {searchQuery || typeFilter !== 'all' ? 'No packs found' : 'No emergency packs yet'}
                  </h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto">
                    {searchQuery || typeFilter !== 'all' 
                      ? 'Try adjusting your search or filter criteria'
                      : 'Create your first emergency information pack to prepare for emergencies'
                    }
                  </p>
                  <Button
                    onClick={() => setShowAddModal(true)}
                    variant="primary"
                    className="flex items-center space-x-2 px-8 py-3 text-lg mx-auto"
                  >
                    <Plus size={20} />
                    <span>Create Your First Pack</span>
                  </Button>
                </div>
              </Card>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPacks.map((pack, index) => {
                const packInfo = packTypeLabels[pack.pack_type];
                const isExpired = pack.expires_at && new Date(pack.expires_at) < new Date();
                
                return (
                  <motion.div
                    key={pack.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card 
                      className="h-full cursor-pointer transition-all duration-300 hover:shadow-xl border-0 bg-white overflow-hidden"
                      onClick={() => setSelectedPack(pack)}
                    >
                      {/* Pack Header */}
                      <div className={`h-24 bg-gradient-to-br from-amber-500 to-amber-600 relative`}>
                        <div className="absolute inset-0 bg-black/10"></div>
                        <div className="absolute top-4 right-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${packInfo.color}`}>
                            {packInfo.icon}
                            <span className="ml-1">{packInfo.label}</span>
                          </span>
                        </div>
                        {isExpired && (
                          <div className="absolute bottom-4 left-4">
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-500 text-white">
                              Expired
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Pack Details */}
                      <div className="p-6">
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{pack.name}</h3>
                            {pack.description && (
                              <p className="text-gray-600 text-sm line-clamp-2">{pack.description}</p>
                            )}
                          </div>

                          {/* Expiry */}
                          {pack.expires_at && (
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <AlertCircle size={14} />
                              <span>Expires: {new Date(pack.expires_at).toLocaleDateString()}</span>
                            </div>
                          )}

                          {/* Permissions */}
                          {pack.permissions && (
                            <div className="text-xs text-gray-500">
                              {pack.permissions.readOnly ? 'Read-only' : 'Editable'} • {pack.permissions.recipientIds?.length || 0} recipients
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="flex space-x-2">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingPack(pack);
                                }}
                                variant="outline"
                                size="sm"
                                className="flex items-center space-x-1"
                              >
                                <Edit size={14} />
                                <span>Edit</span>
                              </Button>
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePack(pack.id);
                                }}
                                variant="outline"
                                size="sm"
                                className="flex items-center space-x-1 text-red-600 border-red-200 hover:bg-red-50"
                              >
                                <Trash2 size={14} />
                                <span>Delete</span>
                              </Button>
                            </div>
                            {pack.share_token && (
                              <div className="text-xs text-gray-500 font-mono">
                                {pack.share_token.substring(0, 8)}...
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </AnimatePresence>

        {/* Pack Detail Modal */}
        {selectedPack && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPack(null)}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedPack.name}</h2>
                  <button onClick={() => setSelectedPack(null)} className="text-gray-400 hover:text-gray-600">
                    <AlertCircle size={24} />
                  </button>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${packTypeLabels[selectedPack.pack_type].color}`}>
                      {packTypeLabels[selectedPack.pack_type].icon}
                      <span className="ml-1">{packTypeLabels[selectedPack.pack_type].label}</span>
                    </span>
                  </div>

                  {selectedPack.description && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                      <p className="text-gray-700">{selectedPack.description}</p>
                    </div>
                  )}

                  {selectedPack.expires_at && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Expiry</h3>
                      <p className="text-gray-700">
                        {new Date(selectedPack.expires_at).toLocaleString()}
                        {new Date(selectedPack.expires_at) < new Date() && (
                          <span className="ml-2 text-red-600 font-semibold">(Expired)</span>
                        )}
                      </p>
                    </div>
                  )}

                  {selectedPack.share_token && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Share Token</h3>
                      <p className="text-gray-700 font-mono text-sm">{selectedPack.share_token}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Pack Modal - Simplified for now */}
        {(showAddModal || editingPack) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowAddModal(false); setEditingPack(null); }}>
            <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingPack ? 'Edit Emergency Pack' : 'Create Emergency Pack'}
              </h2>
              <p className="text-gray-600 mb-4">
                Pack creation form will be implemented here. For now, you can manage packs through the database.
              </p>
              <div className="flex justify-end gap-4">
                <Button variant="outline" onClick={() => { setShowAddModal(false); setEditingPack(null); }}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2 mt-6">
            <AlertCircle size={20} />
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

